import React from 'react';
import { Loader2, Clock, CheckSquare, CheckCircle } from 'lucide-react';

const MONTH_LABELS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

// All available years (2017 through current year)
const NOW = new Date();
export const ALL_YEARS = Array.from({ length: NOW.getFullYear() - 2016 }, (_, i) => 2017 + i);

// Default: last 5 full years + current year
export function getDefaultYears() {
  const cur = NOW.getFullYear();
  return ALL_YEARS.filter(y => y >= cur - 5);
}

// Generate month keys for a set of years
export function getMonthsForYears(years) {
  const months = [];
  const now = new Date();
  for (const year of years.sort((a, b) => a - b)) {
    for (let m = 0; m < 12; m++) {
      if (year > now.getFullYear() || (year === now.getFullYear() && m > now.getMonth())) break;
      months.push(`${year}-${String(m + 1).padStart(2, '0')}`);
    }
  }
  return months;
}

function CellHalf({ pos, cellKey, selected, jobStatus, justFilled, onToggle, half, month, type, completenessPct, expectedCount, surveyedNoData, censusExpected, csvOnly, scrapedEmpty, hasDetails, hasLines, fullDetail, detailPct, extractionMethod }) {
  const isQueued = jobStatus === 'pending' || jobStatus === 'claimed';
  const isRunning = jobStatus === 'running';
  const justDone = jobStatus === 'completed' || jobStatus === 'partial';
  const isFreshFill = justFilled && justFilled.has(cellKey);
  const hasData = pos > 0;

  // Cell color is driven primarily by ingestion method so the user can see
  // at a glance which months are covered by the best (bulk_export) pipeline
  // vs older individual/list-only scrapes or CSV imports.
  const methodClass = {
    bulk_export: 'sd-acm-cal-method-bulk',
    individual: 'sd-acm-cal-method-individual',
    list_only: 'sd-acm-cal-method-list',
    csv: 'sd-acm-cal-method-csv',
  }[extractionMethod];

  let cls;
  if (!hasData) {
    if (scrapedEmpty && methodClass) cls = methodClass;
    else if (scrapedEmpty) cls = 'sd-acm-cal-scraped-empty';
    else if (surveyedNoData) cls = 'sd-acm-cal-surveyed';
    else cls = 'sd-acm-cal-empty';
  } else if (methodClass) {
    cls = methodClass;
  } else if (csvOnly) {
    cls = 'sd-acm-cal-method-csv';
  } else if (fullDetail || hasLines) {
    cls = 'sd-acm-cal-method-bulk';
  } else if (hasDetails) {
    cls = 'sd-acm-cal-method-individual';
  } else if (completenessPct !== null && completenessPct !== undefined) {
    cls = completenessPct >= 95 ? 'sd-acm-cal-complete' : 'sd-acm-cal-partial';
  } else {
    cls = 'sd-acm-cal-method-list';
  }
  if (isFreshFill) cls = 'sd-acm-cal-just-filled';
  else if (justDone) cls = 'sd-acm-cal-done';
  if (isQueued) cls = 'sd-acm-cal-queued';
  if (isRunning) cls = 'sd-acm-cal-running';
  if (selected) cls += ' sd-acm-cal-selected';

  const methodLabel = {
    bulk_export: '✓ Bulk export',
    individual: 'Individual detail scrape',
    list_only: 'List-only scrape',
    csv: 'CSV import',
  }[extractionMethod];

  const csvStr = csvOnly && !methodLabel ? ' · CSV import (scrape to type-tag)' : '';
  const methodStr = methodLabel ? ` · ${methodLabel}` : '';
  const scrapedEmptyStr = scrapedEmpty
    ? (extractionMethod === 'bulk_export' ? ' (0 POs matched)' : ' · Scraped, no results')
    : '';
  const detailStr = (hasData && fullDetail && extractionMethod !== 'bulk_export') ? ` · Has line items (${detailPct || 0}%)`
    : hasLines && !methodLabel ? ` · Has line items (${detailPct || 0}% extracted)`
    : hasDetails && !methodLabel ? ` · Has headers, needs line items (${detailPct || 0}%)`
    : '';
  const completenessStr = completenessPct !== null && completenessPct !== undefined
    ? ` · ${completenessPct.toFixed(0)}% complete${expectedCount ? ` (${pos}/${expectedCount} POs)` : ''}`
    : surveyedNoData && censusExpected
    ? ` · surveyed: ${censusExpected} POs expected, not yet scraped`
    : '';
  const statusStr = isQueued ? ' (queued)' : isRunning ? ' (scraping)' : justDone ? ' (just completed)' : '';

  return (
    <div
      className={`sd-acm-cal-half sd-acm-cal-${half} ${cls}`}
      onClick={(e) => { e.stopPropagation(); onToggle(cellKey); }}
      title={`${month} ${type}: ${pos} POs${methodStr}${detailStr}${completenessStr}${csvStr}${scrapedEmptyStr}${statusStr}`}
    >
      {isRunning && <Loader2 size={10} className="sd-spin" />}
      {isQueued && <Clock size={10} />}
      {justDone && !isFreshFill && <CheckCircle size={10} />}
      {isFreshFill && <span className="sd-acm-cal-count sd-acm-cal-count-animate">{pos}</span>}
      {!isQueued && !isRunning && !justDone && !isFreshFill && hasData && <span className="sd-acm-cal-count">{pos}</span>}
      {!isQueued && !isRunning && !justDone && !isFreshFill && !hasData && scrapedEmpty && <span className="sd-acm-cal-dash">—</span>}
      {selected && !isQueued && !isRunning && !justDone && !isFreshFill && <span className="sd-acm-cal-check">&#10003;</span>}
    </div>
  );
}

export default function CoverageCalendar({ monthMap, selected, jobStatuses, justFilled, onToggle, onSelectAllEmpty, activeYears, onToggleYear }) {
  const now = new Date();
  const sortedYears = [...activeYears].sort((a, b) => a - b);

  return (
    <div className="sd-acm-cal">
      {/* Toolbar: year picker + select all empty */}
      <div className="sd-acm-cal-toolbar">
        <div className="sd-acm-cal-years">
          {ALL_YEARS.map(y => (
            <button
              key={y}
              className={`sd-acm-cal-year-pill ${activeYears.includes(y) ? 'sd-acm-cal-year-active' : ''}`}
              onClick={() => onToggleYear(y)}
            >
              {y}
            </button>
          ))}
        </div>
        <button className="sd-acm-cal-quick" onClick={onSelectAllEmpty}>
          <CheckSquare size={12} /> Select All Empty
        </button>
      </div>

      {/* Column headers */}
      <div className="sd-acm-cal-header">
        <div className="sd-acm-cal-year-label" />
        {MONTH_LABELS.map(m => (
          <div key={m} className="sd-acm-cal-month-label">{m}</div>
        ))}
      </div>

      {/* Year rows */}
      {sortedYears.map(year => (
        <div key={year} className="sd-acm-cal-row">
          <div className="sd-acm-cal-year-label">{year}</div>
          {Array.from({ length: 12 }, (_, i) => {
            const monthKey = `${year}-${String(i + 1).padStart(2, '0')}`;
            const isFuture = year > now.getFullYear() || (year === now.getFullYear() && i > now.getMonth());
            if (isFuture) return <div key={monthKey} className="sd-acm-cal-cell sd-acm-cal-future" />;

            const monthData = monthMap[monthKey];
            const goodsData = monthData?.acq_types?.['IT Goods'] || {};
            const servicesData = monthData?.acq_types?.['IT Services'] || {};
            const goodsPos = goodsData.pos || 0;
            const servicesPos = servicesData.pos || 0;
            const goodsKey = `${monthKey}:IT Goods`;
            const servicesKey = `${monthKey}:IT Services`;
            // Completeness is per-month (not per type) from scprs_runs
            const completenessPct = monthData?.completeness_pct ?? null;
            const expectedCount = monthData?.expected_count ?? null;
            const censusExpected = monthData?.census_expected ?? null;
            const surveyedNoData = monthData?.data_quality === 'surveyed_no_data';

            return (
              <div key={monthKey} className="sd-acm-cal-cell">
                <CellHalf
                  pos={goodsPos}
                  cellKey={goodsKey}
                  selected={selected.has(goodsKey)}
                  jobStatus={jobStatuses[goodsKey]}
                  justFilled={justFilled}
                  onToggle={onToggle}
                  half="top"
                  month={monthKey}
                  type="IT Goods"
                  completenessPct={completenessPct}
                  expectedCount={expectedCount}
                  surveyedNoData={surveyedNoData}
                  censusExpected={censusExpected}
                  csvOnly={goodsData.csv_only}
                  scrapedEmpty={goodsData.scraped_empty}
                  hasDetails={goodsData.has_details}
                  hasLines={goodsData.has_lines}
                  fullDetail={goodsData.full_detail}
                  detailPct={goodsData.detail_pct}
                  extractionMethod={goodsData.extraction_method}
                />
                <CellHalf
                  pos={servicesPos}
                  cellKey={servicesKey}
                  selected={selected.has(servicesKey)}
                  jobStatus={jobStatuses[servicesKey]}
                  justFilled={justFilled}
                  onToggle={onToggle}
                  half="bot"
                  month={monthKey}
                  type="IT Services"
                  completenessPct={completenessPct}
                  expectedCount={expectedCount}
                  surveyedNoData={surveyedNoData}
                  censusExpected={censusExpected}
                  csvOnly={servicesData.csv_only}
                  scrapedEmpty={servicesData.scraped_empty}
                  hasDetails={servicesData.has_details}
                  hasLines={servicesData.has_lines}
                  fullDetail={servicesData.full_detail}
                  detailPct={servicesData.detail_pct}
                  extractionMethod={servicesData.extraction_method}
                />
              </div>
            );
          })}
        </div>
      ))}

      <div className="sd-acm-cal-legend">
        {[
          ['sd-acm-cal-method-bulk', 'Bulk export (best)'],
          ['sd-acm-cal-method-individual', 'Individual detail scrape'],
          ['sd-acm-cal-method-list', 'List-only scrape'],
          ['sd-acm-cal-method-csv', 'CSV import'],
          ['sd-acm-cal-surveyed', 'Surveyed, not scraped'],
          ['sd-acm-cal-scraped-empty', 'Scraped, no results'],
          ['sd-acm-cal-empty', 'No data'],
          ['sd-acm-cal-queued', 'Queued'],
          ['sd-acm-cal-running', 'Scraping'],
          ['sd-acm-cal-selected', 'Selected'],
        ].map(([cls, label]) => (
          <span key={label} className="sd-acm-cal-legend-item">
            <span className={`sd-acm-cal-legend-swatch ${cls}`} /> {label}
          </span>
        ))}
        <span className="sd-acm-cal-legend-hint">Top = IT Goods · Bottom = IT Services</span>
      </div>
    </div>
  );
}
